import { Alert, Anchor, Button, List, Stack, Text, Title } from "@mantine/core";
import { Link, useNavigate  } from "@tanstack/react-router";

interface PrivacyContentProps {
  showContactButton?: boolean;
}

/**
 * Privacy Policy Content Component
 *
 * Comprehensive privacy disclosures for GDPR, CCPA, Colorado Privacy Act, and COPPA compliance.
 */
export function PrivacyContent({
  showContactButton = true,
}: PrivacyContentProps) {
  const navigate = useNavigate();

  return (
    <Stack gap="xl">
      <Alert color="blue" title="Effective Date">
        February 5, 2026
      </Alert>

      <Text size="lg">
        Your privacy is important to Turtle-O Tech, Ltd. (&quot;we&quot;, &quot;us&quot;, or &quot;Anchor&quot;).
        This Privacy Policy explains how we collect, use, share, and protect your personal information
        when you use our task planning application.
      </Text>

      <Stack gap="md">
        <Title order={3}>1. Information We Collect</Title>

        <Title order={4} size="h5">1.1 Account Information</Title>
        <Text>When you create an Anchor account, we collect:</Text>
        <List>
          <List.Item>Email address (required for authentication)</List.Item>
          <List.Item>First and last name (optional)</List.Item>
          <List.Item>Age range (optional, for analytics)</List.Item>
          <List.Item>Postal code and country (optional, for localization)</List.Item>
        </List>

        <Title order={4} size="h5" mt="md">1.2 Sub-Profile Information</Title>
        <Text>
          When you create sub-profiles for household members (e.g., family members), we collect:
        </Text>
        <List>
          <List.Item>First name</List.Item>
          <List.Item>Age range (to enable age-appropriate privacy protections)</List.Item>
          <List.Item>PIN hash (encrypted, for profile switching)</List.Item>
        </List>
        <Alert color="yellow" title="Sub-Profiles & Children" mt="sm">
          <Text size="sm">
            Sub-profiles may be used by children under your supervision. All data collected through
            sub-profiles is owned and controlled by the primary account holder (you). We do not
            knowingly collect personal information directly from children under 13 without verifiable
            parental consent.
          </Text>
        </Alert>

        <Title order={4} size="h5" mt="md">1.3 Task & Content Data</Title>
        <List>
          <List.Item>Tasks you create (description, due date, status, assignee)</List.Item>
          <List.Item>Asset inventory data (home, vehicles, pets, self-care interests)</List.Item>
          <List.Item>Task completion records (who completed, when)</List.Item>
          <List.Item>Custom task templates and catalogs</List.Item>
        </List>

        <Title order={4} size="h5" mt="md">1.4 Usage Analytics (If You Consent)</Title>
        <Text>
          With your consent, we collect analytics data through PostHog to improve Anchor:
        </Text>
        <List>
          <List.Item>Page views and navigation patterns</List.Item>
          <List.Item>Feature usage (which features you use and how often)</List.Item>
          <List.Item>Error logs (sanitized to remove personally identifiable information)</List.Item>
          <List.Item>
            Session recordings (screen replays for debugging, automatically disabled for child profiles)
          </List.Item>
        </List>
        <Text size="sm" c="dimmed" mt="sm">
          You can opt-out of analytics, error tracking, and session recording in your{" "}
          <Anchor component={Link} to="/privacy-settings">
            Privacy Settings
          </Anchor>.
        </Text>
      </Stack>

      <Stack gap="md">
        <Title order={3}>2. How We Collect Information</Title>
        <List>
          <List.Item>
            <strong>Directly from you:</strong> When you create an account, fill out forms,
            or enter tasks and content.
          </List.Item>
          <List.Item>
            <strong>Automatically:</strong> Through cookies and similar technologies when you
            use Anchor (see our{" "}
            <Anchor component={Link} to="/cookie-policy">
              Cookie Policy
            </Anchor>
            ).
          </List.Item>
          <List.Item>
            <strong>From your device:</strong> Device type, browser, operating system (for
            compatibility and debugging).
          </List.Item>
        </List>
      </Stack>

      <Stack gap="md">
        <Title order={3}>3. How We Use Your Information</Title>
        <List>
          <List.Item>
            <strong>Provide Services:</strong> To enable task management, reminders, and core app functionality.
          </List.Item>
          <List.Item>
            <strong>Personalization:</strong> To customize your experience based on preferences and usage patterns.
          </List.Item>
          <List.Item>
            <strong>Improve Anchor:</strong> To analyze usage, identify bugs, and develop new features.
          </List.Item>
          <List.Item>
            <strong>Security:</strong> To prevent fraud, unauthorized access, and ensure platform security.
          </List.Item>
          <List.Item>
            <strong>Communications:</strong> To send service notifications, updates, and respond to support requests.
          </List.Item>
        </List>
      </Stack>

      <Stack gap="md">
        <Title order={3}>4. Third-Party Services & Data Sharing</Title>
        <Text>We use the following third-party services to operate Anchor:</Text>

        <Title order={4} size="h5" mt="sm">4.1 Supabase (Database & Authentication)</Title>
        <List>
          <List.Item>Purpose: Store user accounts, tasks, and application data</List.Item>
          <List.Item>Data Shared: All user-generated content (tasks, profiles, assets)</List.Item>
          <List.Item>Location: United States (AWS infrastructure)</List.Item>
          <List.Item>
            <Anchor href="https://supabase.com/privacy" target="_blank" rel="noopener noreferrer">
              Supabase Privacy Policy
            </Anchor>
          </List.Item>
        </List>

        <Title order={4} size="h5" mt="sm">4.2 PostHog (Analytics & Error Tracking)</Title>
        <List>
          <List.Item>Purpose: Usage analytics, error tracking, session recordings (if consented)</List.Item>
          <List.Item>Data Shared: User ID (pseudonymous), events, error logs, session recordings</List.Item>
          <List.Item>Location: United States (Google Cloud)</List.Item>
          <List.Item>
            PII Protection: Error logs are sanitized; session recording disabled for children
          </List.Item>
          <List.Item>
            <Anchor href="https://posthog.com/privacy" target="_blank" rel="noopener noreferrer">
              PostHog Privacy Policy
            </Anchor>
          </List.Item>
        </List>

        <Title order={4} size="h5" mt="sm">4.3 Netlify (Hosting)</Title>
        <List>
          <List.Item>Purpose: Application hosting and content delivery</List.Item>
          <List.Item>Data Shared: None (static hosting only)</List.Item>
          <List.Item>
            <Anchor href="https://www.netlify.com/privacy/" target="_blank" rel="noopener noreferrer">
              Netlify Privacy Policy
            </Anchor>
          </List.Item>
        </List>

        <Alert color="blue" mt="sm">
          <Text size="sm" fw="bold">
            We do NOT sell your personal information.
          </Text>
        </Alert>
      </Stack>

      <Stack gap="md">
        <Title order={3}>5. Cookies & Tracking Technologies</Title>
        <Text>
          We use cookies and similar technologies (like localStorage) for authentication,
          preferences, and analytics. For complete details, see our{" "}
          <Anchor component={Link} to="/cookie-policy">
            Cookie Policy
          </Anchor>.
        </Text>
      </Stack>

      <Stack gap="md">
        <Title order={3}>6. Your Privacy Rights</Title>

        <Title order={4} size="h5">6.1 Colorado Privacy Act Rights</Title>
        <Text>If you are a Colorado resident, you have the right to:</Text>
        <List>
          <List.Item>
            <strong>Access:</strong> Request a copy of the personal information we hold about you.
          </List.Item>
          <List.Item>
            <strong>Correct:</strong> Request correction of inaccurate information.
          </List.Item>
          <List.Item>
            <strong>Delete:</strong> Request deletion of your personal information (subject to legal exceptions).
          </List.Item>
          <List.Item>
            <strong>Opt-Out:</strong> Opt-out of targeted advertising (we do not engage in this) or sale of data (we do not sell data).
          </List.Item>
          <List.Item>
            <strong>Data Portability:</strong> Receive your data in a portable format.
          </List.Item>
        </List>
        <Text mt="sm">
          To exercise your rights, contact us at{" "}
          <Anchor href="mailto:admin@yourlifeanchor.com">admin@yourlifeanchor.com</Anchor>.
        </Text>

        <Title order={4} size="h5" mt="md">6.2 CCPA Rights (California Residents)</Title>
        <Text>
          California residents have similar rights under the California Consumer Privacy Act.
          Contact us to exercise CCPA rights.
        </Text>
      </Stack>

      <Stack gap="md">
        <Title order={3}>7. Sub-Profiles & Children&apos;s Privacy</Title>

        <Title order={4} size="h5">7.1 Parent-Mediated Access Model</Title>
        <Text>
          Anchor allows primary account holders (adults 18+) to create sub-profiles for household
          members, including children. This is a <strong>parent-mediated access model</strong>:
        </Text>
        <List>
          <List.Item>Children access Anchor through the parent&apos;s device and account</List.Item>
          <List.Item>All data collected through sub-profiles is owned by the primary account holder</List.Item>
          <List.Item>Parents control all sub-profile data and can delete it at any time</List.Item>
        </List>

        <Title order={4} size="h5" mt="md">7.2 Child Privacy Protections</Title>
        <Text>
          When a sub-profile is identified as a child (age range under 18):
        </Text>
        <List>
          <List.Item>Session recording is automatically disabled</List.Item>
          <List.Item>Analytics events are logged under the parent&apos;s account</List.Item>
          <List.Item>No direct marketing or targeted advertising</List.Item>
        </List>

        <Title order={4} size="h5" mt="md">7.3 COPPA Compliance</Title>
        <Text>
          We do not knowingly collect personal information directly from children under 13 without
          verifiable parental consent. Our parent-mediated model ensures parents control all data
          collection. If you believe we have inadvertently collected information from a child without
          consent, contact us immediately.
        </Text>
      </Stack>

      <Stack gap="md">
        <Title order={3}>8. Data Security</Title>
        <Text>We protect your information using industry-standard measures:</Text>
        <List>
          <List.Item>Encryption in transit (HTTPS/TLS)</List.Item>
          <List.Item>Encryption at rest (database-level encryption via Supabase)</List.Item>
          <List.Item>Row-Level Security (RLS) policies to isolate user data</List.Item>
          <List.Item>PIN-based sub-profile access (hashed and salted)</List.Item>
          <List.Item>Regular security audits and updates</List.Item>
        </List>
        <Text mt="sm" size="sm" c="dimmed">
          However, no system is 100% secure. Please use strong passwords and protect your account credentials.
        </Text>
      </Stack>

      <Stack gap="md">
        <Title order={3}>9. Data Retention</Title>
        <Text>We retain your information for as long as:</Text>
        <List>
          <List.Item>Your account is active</List.Item>
          <List.Item>Needed to provide services</List.Item>
          <List.Item>Required by law (e.g., financial records, compliance)</List.Item>
        </List>
        <Text mt="sm">
          When you delete your account, we delete your personal information within 30 days,
          except where retention is required by law.
        </Text>
      </Stack>

      <Stack gap="md">
        <Title order={3}>10. International Users</Title>
        <Text>
          Anchor is operated from the United States. If you access Anchor from outside the US,
          your information will be transferred to and stored in the United States, which may have
          different data protection laws than your jurisdiction.
        </Text>
      </Stack>

      <Stack gap="md">
        <Title order={3}>11. Changes to This Privacy Policy</Title>
        <Text>
          We may update this Privacy Policy from time to time to reflect changes in our practices
          or legal requirements. We will notify you of material changes by:
        </Text>
        <List>
          <List.Item>Posting the updated policy on this page with a new effective date</List.Item>
          <List.Item>Sending a notification through the app or via email</List.Item>
        </List>
        <Text mt="sm">
          Continued use of Anchor after changes constitutes acceptance of the updated policy.
        </Text>
      </Stack>

      <Stack gap="md">
        <Title order={3}>12. Contact Us</Title>
        <Text>
          If you have questions, concerns, or requests regarding this Privacy Policy or your data,
          please contact us:
        </Text>
        <List>
          <List.Item>
            Email:{" "}
            <Anchor href="mailto:admin@yourlifeanchor.com">admin@yourlifeanchor.com</Anchor>
          </List.Item>
          <List.Item>Company: Turtle-O Tech, Ltd.</List.Item>
          <List.Item>State: Colorado, United States</List.Item>
        </List>

        {showContactButton && (
          <Button
            onClick={() => navigate({ to: "/feedback" })}
            mt="lg"
            variant="filled"
          >
            Contact Support
          </Button>
        )}
      </Stack>

    </Stack>
  );
}
